import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { resetUserScopedStores } from './resetUserScoped';

// Module-level memoization: restoreSession runs exactly once per page load.
// Protects against React StrictMode double-invocation of AuthGuard's effect
// (which otherwise flips loading true→false twice and causes a visible
// flicker on OAuth-callback returns).
let restorePromise: Promise<void> | null = null;
let authListenerAttached = false;

// Decode the `user_role` claim from a Supabase access token (set by the
// custom_access_token_hook). Returns null on any malformed input.
function decodeUserRole(token: string | null): string | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (padded.length % 4)) % 4);
    const payload = JSON.parse(atob(padded + padding));
    return typeof payload.user_role === 'string' ? payload.user_role : null;
  } catch {
    return null;
  }
}

interface AuthState {
  token: string | null;
  user: { id: string; email: string } | null;
  userRole: string | null;
  loading: boolean;
  error: string;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signUpAsLead: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (redirectTo: string) => Promise<void>;
  refreshSession: () => Promise<void>;
  /** `scope` defaults to Supabase's global sign-out (all devices). Pass 'local'
   *  for sign-outs the user did not ask for — see api/client handleUnauthorized. */
  signOut: (scope?: 'global' | 'local') => Promise<void>;
  restoreSession: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  setNewPassword: (newPassword: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  token: null,
  user: null,
  userRole: null,
  loading: true,
  error: '',

  signIn: async (email, password) => {
    set({ loading: true, error: '' });
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      set({ loading: false, error: error.message });
      return;
    }
    const tok = data.session?.access_token ?? null;
    set({
      token: tok,
      userRole: decodeUserRole(tok),
      user: data.user ? { id: data.user.id, email: data.user.email ?? '' } : null,
      loading: false,
      error: '',
    });
  },

  signUp: async (email, password) => {
    set({ loading: true, error: '' });
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) {
      set({ loading: false, error: error.message });
      return;
    }
    const tok = data.session?.access_token ?? null;
    set({
      token: tok,
      userRole: decodeUserRole(tok),
      user: data.user ? { id: data.user.id, email: data.user.email ?? '' } : null,
      loading: false,
      error: '',
    });
  },

  signUpAsLead: async (email, password) => {
    set({ loading: true, error: '' });
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { source_app: 'lead-portal' } },
    });
    if (error) {
      set({ loading: false, error: error.message });
      return;
    }
    const tok = data.session?.access_token ?? null;
    set({
      token: tok,
      userRole: decodeUserRole(tok),
      user: data.user ? { id: data.user.id, email: data.user.email ?? '' } : null,
      loading: false,
      error: '',
    });
  },

  signInWithGoogle: async (redirectTo) => {
    set({ loading: true, error: '' });
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo, scopes: 'email profile' },
    });
    if (error) {
      set({ loading: false, error: error.message });
    }
    // On success the browser is navigating away — leave loading=true.
  },

  refreshSession: async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session) return;
    set({
      token: data.session.access_token,
      userRole: decodeUserRole(data.session.access_token),
      user: data.session.user
        ? { id: data.session.user.id, email: data.session.user.email ?? '' }
        : null,
      loading: false,
      error: '',
    });
  },

  signOut: async (scope = 'global') => {
    // Tell Supabase to invalidate the session server-side and remove its
    // OWN auth-token entry from localStorage. The default scope is global,
    // so this revokes the refresh token across all devices — right for the
    // logout button, wrong for a sign-out forced by a failed request, which
    // passes 'local' and leaves the user's other devices alone.
    await supabase.auth.signOut({ scope });

    // Defensive cleanup: remove ANY leftover Supabase-shaped auth keys.
    // The Supabase client only knows about the storage key tied to its
    // currently-configured `VITE_SUPABASE_URL`. If that URL ever changed
    // (e.g. local → production Supabase), the previous client's key
    // (`sb-<old-project-ref>-auth-token`) is orphaned in localStorage and
    // can be picked up by stale code paths that read raw storage. Nuke
    // every `sb-*-auth-token` key on logout so the user is fully clean.
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stale: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const k = window.localStorage.key(i);
          if (k && /^sb-.*-auth-token$/.test(k)) stale.push(k);
        }
        for (const k of stale) window.localStorage.removeItem(k);
      }
    } catch {
      // localStorage can throw in some private/embedded contexts — best effort.
    }

    // Wipe per-user store state (connection statuses, audit data, report
    // tabs, queued analysis, etc.) so the next user starts clean. Done
    // BEFORE the auth listener's SIGNED_OUT event fires so the UI flips
    // to the login screen with no stale data flicker.
    resetUserScopedStores();

    set({ token: null, user: null, userRole: null, error: '' });
  },

  requestPasswordReset: async (email) => {
    // Anti-enumeration: Supabase already responds 200 OK regardless of
    // whether the email exists, so the caller can always show the same
    // "if that email is in our system, you'll get a link" copy. We only
    // surface an error here for genuine failures (network / 5xx).
    set({ loading: true, error: '' });
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      set({ loading: false, error: error.message });
      return;
    }
    set({ loading: false, error: '' });
  },

  setNewPassword: async (newPassword) => {
    // Caller is responsible for ensuring a recovery session exists (the
    // /reset-password page consumes the `#access_token=...&type=recovery`
    // hash via supabase.auth.getSession() on mount). Without that session
    // updateUser() will return a 401 which we surface as the error.
    set({ loading: true, error: '' });
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) {
      set({ loading: false, error: error.message });
      return;
    }
    // updateUser does not change user.id; the onAuthStateChange listener
    // will pick up the refreshed session and update token + user in store.
    set({
      loading: false,
      error: '',
      user: data.user ? { id: data.user.id, email: data.user.email ?? '' } : null,
    });
  },

  restoreSession: async () => {
    // Return the in-flight / completed promise so repeated calls (StrictMode
    // double-mount, AuthGuard remount) coalesce into one execution.
    if (restorePromise) return restorePromise;

    restorePromise = (async () => {
      set({ loading: true });
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        set({
          token: data.session.access_token,
          userRole: decodeUserRole(data.session.access_token),
          user: data.session.user
            ? { id: data.session.user.id, email: data.session.user.email ?? '' }
            : null,
          loading: false,
        });
      } else {
        set({ loading: false });
      }

      // Attach the auth listener exactly once per page load.
      if (!authListenerAttached) {
        authListenerAttached = true;
        supabase.auth.onAuthStateChange((_event, session) => {
          if (session) {
            // Detect account switch: a different user.id replacing the
            // previous one (or a sign-in from a logged-out tab where
            // store state outlived the page via sessionStorage / locally
            // persisted Zustand slices). Token refreshes keep the same
            // user.id and must NOT trigger a reset.
            const prevUserId = useAuthStore.getState().user?.id ?? null;
            const nextUserId = session.user?.id ?? null;
            if (nextUserId && nextUserId !== prevUserId) {
              resetUserScopedStores();
            }
            set({
              token: session.access_token,
              userRole: decodeUserRole(session.access_token),
              user: session.user
                ? { id: session.user.id, email: session.user.email ?? '' }
                : null,
            });
          } else {
            // SIGNED_OUT — clear per-user store state in addition to auth.
            // Also handles the case where Supabase invalidates the session
            // externally (token revoked, password changed elsewhere).
            resetUserScopedStores();
            set({ token: null, user: null, userRole: null });
          }
        });
      }
    })();

    return restorePromise;
  },
}));
